/* eslint-disable */
import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Container, Divider, Grid, MenuItem, Select, Switch, Typography, styled, useTheme } from "@mui/material";
import { PlayArrow as PlayArrowIcon, Timeline as TimelineIcon } from "@mui/icons-material";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { GET_AGENT_CONFIG, GET_AGENT_TASK_LOGS, GET_AGENT_SUMMARY } from "../../gql/queries/ai";
import { UPDATE_AGENT_CONFIG, RUN_AUTOPILOT_AGENT } from "../../gql/mutations/ai";
import AiPageHeader from "./AiPageHeader";

const SectionWrapper = styled("section")(({ theme }) => ({
  minHeight: "calc(100vh - 70px)",
  backgroundColor: theme.palette.background.default,
  paddingBottom: "60px",
}));

const SUB_AGENTS = [];

function AiAutopilot() {
  const theme = useTheme();
  const userData = useSelector(state => state.reducer.userData);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") || "";

  const [localProjectId, setLocalProjectId] = useState(projectId || "");
  const [isEnabled, setIsEnabled] = useState(false);
  const [subAgents, setSubAgents] = useState({});
  const [schedule, setSchedule] = useState("daily");
  const [configLoaded, setConfigLoaded] = useState(false);

  const [fetchConfig, { data: configData, loading: configLoading }] = useLazyQuery(GET_AGENT_CONFIG, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (data?.getAgentConfig) {
        const config = data.getAgentConfig;
        setIsEnabled(config.isEnabled || false);
        setSchedule(config.schedule || "daily");
        if (config.subAgents) {
          try {
            const parsed = typeof config.subAgents === "string" ? JSON.parse(config.subAgents) : config.subAgents;
            if (typeof parsed === "object" && parsed !== null) {
              setSubAgents(prev => ({ ...prev, ...parsed }));
            }
          } catch {
            // keep defaults
          }
        }
        setConfigLoaded(true);
      }
    },
  });

  const [fetchTaskLogs, { data: logsData, loading: logsLoading }] = useLazyQuery(GET_AGENT_TASK_LOGS, { fetchPolicy: "network-only" });

  const [fetchSummary, { data: summaryData }] = useLazyQuery(GET_AGENT_SUMMARY, {
    fetchPolicy: "network-only",
  });

  const [updateConfig, { loading: updateLoading }] = useMutation(UPDATE_AGENT_CONFIG, {
    onCompleted: () => {
      toast.success("Agent configuration updated!");
    },
    onError: err => {
      toast.error(err.message || "Failed to update configuration");
    },
  });

  const [runAgent, { loading: runLoading }] = useMutation(RUN_AUTOPILOT_AGENT, {
    onCompleted: () => {
      toast.success("Autopilot agent triggered successfully!");
      handleFetchLogs();
    },
    onError: err => {
      toast.error(err.message || "Failed to run agent");
    },
  });

  const activeProjectId = projectId || localProjectId;

  const handleFetchLogs = () => {
    if (activeProjectId) {
      fetchTaskLogs({
        variables: { projectId: activeProjectId, limit: 50 },
      });
    }
  };

  useEffect(() => {
    if (projectId) setLocalProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    if (activeProjectId) {
      fetchConfig({ variables: { projectId: activeProjectId } });
      handleFetchLogs();
      fetchSummary({ variables: { projectId: activeProjectId } });
    }
  }, [activeProjectId]);

  const handleToggleMaster = e => {
    const newVal = e.target.checked;
    setIsEnabled(newVal);
    saveConfig({ isEnabled: newVal });
  };

  const handleToggleSubAgent = key => {
    const updated = { ...subAgents, [key]: !subAgents[key] };
    setSubAgents(updated);
    saveConfig({ subAgents: updated });
  };

  const handleScheduleChange = e => {
    const newSchedule = e.target.value;
    setSchedule(newSchedule);
    saveConfig({ schedule: newSchedule });
  };

  const saveConfig = (overrides = {}) => {
    if (!activeProjectId) {
      toast.error("Please provide a Project ID");
      return;
    }
    updateConfig({
      variables: {
        data: {
          projectId: activeProjectId,
          isEnabled,
          subAgents: JSON.stringify(subAgents),
          schedule,
          ...overrides,
          ...(overrides.subAgents && {
            subAgents: JSON.stringify(overrides.subAgents),
          }),
        },
      },
    });
  };

  const handleRunNow = () => {
    if (!activeProjectId) {
      toast.error("Please provide a Project ID");
      return;
    }
    runAgent({ variables: { projectId: activeProjectId } });
  };

  const formatDate = dateStr => {
    if (!dateStr) return "---";
    try {
      return new Date(Number(dateStr) || dateStr).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = ms => {
    if (!ms) return "---";
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remaining = seconds % 60;
    return `${minutes}m ${remaining}s`;
  };

  const taskStatusConfig = {
    completed: { color: "success", label: "Completed" },
    running: { color: "info", label: "Running" },
    failed: { color: "error", label: "Failed" },
  };

  const getTaskStatusChip = status => {
    const config = taskStatusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} size="small" color={config.color} />;
  };

  const taskLogs = logsData?.getAgentTaskLogs || [];
  const agentConfig = configData?.getAgentConfig;

  return (
    <SectionWrapper>
      <Container maxWidth="">
        <Box sx={{ flexGrow: 1 }}>
          <AiPageHeader
            title="SEO Autopilot"
            description="Configure and monitor your autonomous SEO agent that crawls, fixes, optimizes, and reports automatically."
            projectId={activeProjectId}
            onProjectChange={id => setLocalProjectId(id || "")}
            showProjectSelector
            showQuickLinks
          />

          {configLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Master Switch & Schedule */}
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      padding: "20px",
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: "none",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Autopilot Agent
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Master toggle for the autonomous SEO agent
                        </Typography>
                      </Box>
                      <Switch checked={isEnabled} onChange={handleToggleMaster} color="success" disabled={updateLoading || !activeProjectId} />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Schedule
                      </Typography>
                      <Select size="small" fullWidth value={schedule} onChange={handleScheduleChange} disabled={!isEnabled || !activeProjectId}>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </Box>

                    {agentConfig?.lastRunAt && (
                      <Typography variant="body2" color="text.secondary">
                        Last run: {formatDate(agentConfig.lastRunAt)}
                      </Typography>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={runLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                        onClick={handleRunNow}
                        disabled={runLoading || !activeProjectId}
                        sx={{ textTransform: "none" }}
                      >
                        Run Now
                      </Button>
                    </Box>
                  </Card>
                </Grid>

                {/* Sub-Agent Toggles */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      padding: "20px",
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: "none",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Sub-Agents
                    </Typography>
                    {SUB_AGENTS.map(agent => (
                      <Box
                        key={agent.key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {agent.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {agent.description}
                          </Typography>
                        </Box>
                        <Switch
                          checked={subAgents[agent.key] || false}
                          onChange={() => handleToggleSubAgent(agent.key)}
                          disabled={!isEnabled || updateLoading || !activeProjectId}
                          size="small"
                        />
                      </Box>
                    ))}
                  </Card>
                </Grid>
              </Grid>

              {/* Activity Timeline */}
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  <Card
                    sx={{
                      padding: "20px",
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <TimelineIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Activity Timeline
                      </Typography>
                    </Box>

                    {logsLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : taskLogs.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          {activeProjectId
                            ? "No activity logs yet. Enable the agent and click Run Now to start."
                            : "Select a website above to configure and run the autopilot agent."}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        {taskLogs.map(log => (
                          <Box
                            key={log.id}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                              py: 1.5,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 80,
                                pt: 0.3,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(log.createdAt)}
                              </Typography>
                            </Box>
                            <Box sx={{ minWidth: 100 }}>
                              <Chip label={log.agentType || "unknown"} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {log.action || "---"}
                              </Typography>
                              {log.error && (
                                <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                                  Error: {log.error}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ minWidth: 60, textAlign: "right" }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDuration(log.duration)}
                              </Typography>
                            </Box>
                            <Box sx={{ minWidth: 90 }}>{getTaskStatusChip(log.status)}</Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Container>
    </SectionWrapper>
  );
}

export default AiAutopilot;
